/**
 * Modal Controller Module
 * Manages modal-specific logic and interactions
 */

import type { State } from './types';
import { els, closeEstimateModal, closeCloseTimeModal } from './modules/ui';
import { setCustomCloseTime } from './modules/storage';
import { showToast } from './modules/ui';
import { commitEstimateEdit } from './modules/estimates';
import { applyRemoteSnapshot, mapRemoteDocument } from './modules/lotOperations';
import { createLogger } from '../shared/logger';

const log = createLogger('portal:modal');

interface ModalControllerDeps {
  state: State;
  render: () => void;
  save: () => void;
  ensureAccessToken: (retry?: boolean) => Promise<string | null>;
  applyModeBanner: () => void;
  DEBUG: boolean;
}

/**
 * Handle estimate edit commit from modal
 */
export function createEstimateModalHandler(deps: ModalControllerDeps) {
  return function handleCommitEstimateEditFromModal() {
    const { state, render, save, ensureAccessToken, applyModeBanner, DEBUG } = deps;

    // Validate modal is open and state is ready
    if (!state.estimateEdit) {
      closeEstimateModal();
      return;
    }

    // Extract values from form
    const lowValue =
      els.estimateLowValue instanceof HTMLInputElement ? els.estimateLowValue.value.trim() : '';
    const highValue =
      els.estimateHighValue instanceof HTMLInputElement ? els.estimateHighValue.value.trim() : '';
    const biddingToggle = document.getElementById('estimate-bidding-toggle');
    const bidAmountInput = document.getElementById('estimate-bid-amount');

    // Update state
    state.estimateEdit.low = lowValue;
    state.estimateEdit.high = highValue;

    // Update bidding fields
    if (biddingToggle instanceof HTMLInputElement) {
      state.estimateEdit.bidding = biddingToggle.checked;
      log.debug('Captured bidding toggle:', biddingToggle.checked);
    }

    if (bidAmountInput instanceof HTMLInputElement) {
      state.estimateEdit.bidAmount = bidAmountInput.value.trim();
      log.debug('Captured bid amount:', bidAmountInput.value.trim());
    }

    // Commit the edit
    commitEstimateEdit(
      state,
      render,
      save,
      applyRemoteSnapshot,
      applyModeBanner,
      ensureAccessToken,
      mapRemoteDocument,
      DEBUG
    );

    // Close modal
    closeEstimateModal();
  };
}

/**
 * Handle close time edit commit
 */
export function createCloseTimeCommitHandler(deps: ModalControllerDeps) {
  return function commitCloseTimeEdit() {
    const { state, render } = deps;

    if (!state.closeTimeEdit || !state.closeTimeEdit.id) {
      closeCloseTimeModal();
      return;
    }

    const lotId = state.closeTimeEdit.id;
    const lot = state.lots.find((item: any) => item && item.id === lotId);

    if (!lot) {
      void closeModal();
      return;
    }

    if (els.closeTimeValue instanceof HTMLInputElement) {
      const dateTimeValue = els.closeTimeValue.value;

      if (dateTimeValue) {
        const date = new Date(dateTimeValue);
        if (!isNaN(date.getTime())) {
          const timestamp = date.getTime();
          setCustomCloseTime(lotId, timestamp);
          showToast('Custom close time saved.', { type: 'success' });
        } else {
          showToast('Invalid date/time format.', { type: 'error' });
          return;
        }
      } else {
        setCustomCloseTime(lotId, null);
        showToast('Custom close time cleared.', { type: 'success' });
      }
    }

    render();
    closeCloseTimeModal();
  };
}

/**
 * Handle clear close time edit
 */
export function createCloseTimeClearHandler(deps: ModalControllerDeps) {
  return function clearCloseTimeEdit() {
    const { state, render } = deps;

    if (!state.closeTimeEdit || !state.closeTimeEdit.id) {
      return;
    }

    const lotId = state.closeTimeEdit.id;
    setCustomCloseTime(lotId, null);
    showToast('Custom close time cleared.', { type: 'success' });
    render();
    closeCloseTimeModal();
  };
}

/**
 * Prepare lot form for creating a new lot
 */
export function prepareLotFormForCreate(): void {
  // Reset the lot creation form to a clean state
  if (els.form) {
    (els.form as HTMLFormElement).reset();
  }
  if (els.f?.id) {
    els.f.id.value = '';
  }
  // Clear individual fields explicitly (in case browser retains values)
  if (els.f?.title) els.f.title.value = '';
  if (els.f?.nextRequiredBid) els.f.nextRequiredBid.value = '';
  if (els.f?.close) els.f.close.value = '';
  if (els.f?.url) els.f.url.value = '';
  if (els.f?.description) els.f.description.value = '';
  // Source select resets via form.reset(); leave as-is so default option applies.
}
